import { loader } from "graphql.macro";
import {
  AmplifyGet,
  AmplifyPatch,
  AmplifyPost,
} from "@sunrun/sales-experience-shared/amplify-api-wrapper";
import {
  EditOffer,
  EditOfferBundle,
  EditOfferInitialize,
  EditOfferProductAvailability,
  ListOffer,
  OfferLineCreate,
  OfferLineUpdate,
  OfferSuccess,
  PricingOffer,
  ReviewOffer,
  SyncSalesforceResponse,
} from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import {
  AcpMarketType,
  UserRole,
} from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/constants";
import { gql, storeFront } from "./storeFrontApolloConnection";
import { UnifiedPricingData } from "providers/pricing/PricePoints";
import { NewOfferData } from "providers/Types";
import {
  CASH,
  FLEX_MONTHLY,
  LOAN,
  MONTHLY,
  PREPAID,
} from "constants/financialProducts";
import { logger } from "providers/logger";

const getOffer = async (
  jwt: string,
  offerId: string,
  identity: string
): Promise<ReviewOffer> => {
  return await AmplifyGet<ReviewOffer>(
    "Get Offer",
    "OfferExpApi",
    `/offers/${offerId}?identity=${identity}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const getEditOffer = async (
  jwt: string,
  offerId: string
): Promise<EditOffer> => {
  return await AmplifyGet<EditOffer>(
    "Get Edit Offer",
    "OfferExpApi",
    `/offers/edit/${offerId}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const getPricingOffer = async (
  jwt: string,
  offerId: string
): Promise<PricingOffer> => {
  return await AmplifyGet<PricingOffer>(
    "Get Pricing Offer",
    "OfferExpApi",
    `/offers/pricing/${offerId}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const listOffers = async (
  jwt: string,
  prospectId: string,
  identity: string
): Promise<ListOffer[]> => {
  return await AmplifyGet<ListOffer[]>(
    "Get List Offers",
    "OfferExpApi",
    `/offers/prospect/${prospectId}?identity=${identity}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const getStoreFrontDesignImages = async (
  jwt: string,
  designId: string,
  designVersion: number,
  envOverride?: string
): Promise<any> => {
  const queryString = envOverride
    ? `?envOverride=${encodeURIComponent(envOverride)}`
    : "";
  return await AmplifyGet<any>(
    "Get Store Front Design Images",
    "OfferExpApi",
    `/offers/designImages/${designId}/${designVersion}${queryString}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const customerListOffers = async (
  proposalToken: string
): Promise<ListOffer[]> => {
  return await AmplifyGet<ListOffer[]>(
    "Get Customer List Offers",
    "OfferExpApi",
    `/customer/${proposalToken}`,
    {},
    logger
  );
};

const customerGetOffer = async (
  proposalToken: string,
  offerId: string
): Promise<ReviewOffer> => {
  return await AmplifyGet<ReviewOffer>(
    "Get Customer Offer",
    "OfferExpApi",
    `/customer/${proposalToken}/${offerId}`,
    {},
    logger
  );
};

const customerGetDesignImages = async (
  proposalToken: string,
  designId: string,
  designVersion: number
): Promise<any> => {
  return await AmplifyGet<any>(
    "Get Customer Design Images",
    "OfferExpApi",
    `/customer/${proposalToken}/designImages/${designId}/${designVersion}`,
    {},
    logger
  );
};

// Empty will return an offer that is guaranteed empty, but is not necessarily new
const emptyOffer = async (
  jwt: string,
  prospectId: string,
  updatedFields?: string[],
  unsignedSourceProposalId?: string
): Promise<OfferSuccess> => {
  return await AmplifyPost<OfferSuccess>(
    "Empty Offer",
    "OfferExpApi",
    "/offers/empty",
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        prospectId,
        updatedFields,
        unsignedSourceProposalId,
      },
    },
    logger,
    { expectedErrorCodes: [501] }
  );
};

const createOfferInitialize = async (
  jwt: string,
  newOffer: NewOfferData,
  spanAsAccessoryEnabled: boolean = false
): Promise<EditOfferInitialize> => {
  const products = newOffer.productAvailability;
  const initialBundle = products.bundles[0];
  const initialProductIds = initialBundle
    ? spanAsAccessoryEnabled
      ? Array.from(
          new Set([
            ...initialBundle.productIds,
            ...(initialBundle.requiredAccessories ?? []),
          ])
        )
      : [...initialBundle.productIds]
    : [];
  const initialProducts = products.products.filter((product) =>
    initialProductIds.includes(product.id)
  );
  const initialOfferLines = initialProducts.map((product) => {
    const initialOfferLine = {
      variantId: product.defaultVariantId,
    } as OfferLineCreate;
    if (product.type === "OFF_THE_SHELF") {
      // Required accessories should start at their minimum quantity (>= 1).
      initialOfferLine.quantity = Math.max(product.minQuantity ?? 1, 1);
    }
    return initialOfferLine;
  });
  if (!initialOfferLines || initialOfferLines.length === 0) {
    return {
      offerId: newOffer.offerId,
      offer: undefined,
      products,
    };
  }
  const offerLinesResponse = await createOfferLines(
    jwt,
    newOffer.offerId,
    initialOfferLines,
    newOffer.lockVersion,
    true
  );
  return {
    offerId: offerLinesResponse.id,
    offer: offerLinesResponse,
    products,
  };
};

// Reconciles an existing offer's lines against the active bundle's required
// products (productIds + requiredAccessories). When lines are missing, this
// posts a single createOfferLines request so the offer self-heals on load.
// Returns the updated EditOffer when a request was made, or undefined when
// nothing needed to change.
const reconcileRequiredAccessories = async (
  jwt: string,
  offerId: string,
  bundle: EditOfferBundle | undefined,
  products: EditOfferProductAvailability["products"],
  existingLines: EditOffer["lines"],
  expectedLockVersion: number
): Promise<EditOffer | undefined> => {
  if (!bundle) {
    return undefined;
  }
  const requiredIds = Array.from(
    new Set([...bundle.productIds, ...(bundle.requiredAccessories ?? [])])
  );
  const presentIds = new Set(
    existingLines.map((line) => line.variant.product.id)
  );
  const missingIds = requiredIds.filter((id) => !presentIds.has(id));
  if (missingIds.length === 0) {
    return undefined;
  }
  const linesToCreate: OfferLineCreate[] = missingIds.flatMap((id) => {
    const product = products.find((p) => p.id === id);
    if (!product) {
      return [];
    }
    const line: OfferLineCreate = { variantId: product.defaultVariantId };
    if (product.type === "OFF_THE_SHELF") {
      line.quantity = Math.max(product.minQuantity ?? 1, 1);
    }
    return [line];
  });
  if (linesToCreate.length === 0) {
    return undefined;
  }
  return createOfferLines(jwt, offerId, linesToCreate, expectedLockVersion);
};

const createOfferLines = async (
  jwt: string,
  offerId: string,
  lines: Array<OfferLineCreate>,
  expectedLockVersion: number,
  expectLockFailure: boolean = false
): Promise<EditOffer> => {
  return await AmplifyPost(
    "Create Offer Lines",
    "OfferExpApi",
    `/offers/${offerId}/lines`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        lines,
        expectedLockVersion,
      },
    },
    logger,
    { expectedErrorCodes: expectLockFailure ? [409] : [] }
  );
};

const copyOffer = async (
  jwt: string,
  offerId: string
): Promise<OfferSuccess> => {
  return await AmplifyPost<OfferSuccess>(
    "Copy Offer",
    "OfferExpApi",
    "/offers/copy",
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        offerId,
      },
    },
    logger
  );
};

const updateOfferLines = async (
  jwt: string,
  offerId: string,
  lines: Array<OfferLineUpdate>,
  expectedLockVersion: number,
  designId?: string,
  designVersion?: number
): Promise<EditOffer> => {
  return await AmplifyPatch(
    "Update Offer Lines",
    "OfferExpApi",
    `/offers/${offerId}/lines`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        lines,
        expectedLockVersion,
        designId,
        designVersion,
      },
    },
    logger
  );
};

const deleteOfferLines = async (
  jwt: string,
  offerId: string,
  lineIds: Array<string>,
  expectedLockVersion: number
): Promise<EditOffer> => {
  return await AmplifyPost(
    "Delete Offer Lines",
    "OfferExpApi",
    `/offers/${offerId}/lines/delete`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        lineIds,
        expectedLockVersion,
      },
    },
    logger
  );
};

const deleteOffer = async (
  jwt: string,
  offerId: string,
  expectedLockVersion: number
): Promise<void> => {
  return await AmplifyPost(
    "Delete Offer",
    "OfferExpApi",
    `/offers/${offerId}/delete`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        expectedLockVersion,
      },
    },
    logger
  );
};

const syncSalesforce = async (
  jwt: string,
  prospectId: string,
  parentProposalId?: string,
  signedRootId?: string
): Promise<void> => {
  const res = await AmplifyPost<SyncSalesforceResponse>(
    "Sync Salesforce",
    "OfferExpApi",
    `/offers/prospect/${prospectId}/sync`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        parentProposalId,
        signedRootId,
      },
    },
    logger
  );
  return await new Promise((resolve, reject) => {
    if (!res?.syncOffersFromSalesforceAsync) {
      reject(
        new Error(
          `Error While Syncing No Async Token Given, ${JSON.stringify(res)}`
        )
      );
    } else {
      onDomainEvent(res.syncOffersFromSalesforceAsync).subscribe((res) => {
        const errors = res.data?.onDomainEvent.errors || [];
        if (errors.length) {
          reject(new Error(`Error While Syncing, ${JSON.stringify(errors)}`));
        } else {
          resolve();
        }
      });
    }
  });
};

const amendOffer = async (
  jwt: string,
  prospectId: string,
  parentProposalId: string,
  signedRootId: string
): Promise<string> => {
  return await AmplifyPost(
    "Amend Offer",
    "OfferExpApi",
    `/offers/prospect/${prospectId}/amend`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      body: {
        parentProposalId,
        signedRootId,
      },
    },
    logger
  );
};

const amendOfferWithSync = async (
  jwt: string,
  prospectId: string,
  parentProposalId: string,
  signedRootId: string
): Promise<string> => {
  try {
    return await amendOffer(jwt, prospectId, parentProposalId, signedRootId);
  } catch (_) {
    try {
      await syncSalesforce(jwt, prospectId, parentProposalId, signedRootId);
      return await amendOffer(jwt, prospectId, parentProposalId, signedRootId);
    } catch (_) {
      await syncSalesforce(jwt, prospectId);
      return await amendOffer(jwt, prospectId, parentProposalId, signedRootId);
    }
  }
};

const getProducts = async (
  jwt: string,
  offerId: string,
  state: string,
  market: AcpMarketType,
  ePermitting: boolean,
  identity: string
): Promise<EditOfferProductAvailability> => {
  return await AmplifyGet(
    "Get Products",
    "OfferExpApi",
    `/products/${offerId}?state=${state}&acpMarketType=${market}&ePermitting=${ePermitting}&identity=${identity}`,
    {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
    },
    logger
  );
};

const submitOffer = (
  offerId: string,
  pricePointId: string,
  expectedLockVersion: number,
  oneHost: string,
  evSpanAvailable: boolean
) => {
  let proposalOrigin = "Offer Experience";
  if (evSpanAvailable) {
    proposalOrigin = "Offer Experience D2";
  }
  let host = "One (Offer Experience)";
  if (oneHost === "LIGHTMILE") {
    host = "Lightmile (Offer Experience)";
    proposalOrigin = "Offer Experience D2";
  }
  const submitOfferQuery = gql`
    mutation submitOffer(
      $offerId: ID!
      $pricePointId: ID!
      $expectedLockVersion: Int!
      $host: String!
      $proposalOrigin: String!
    ) {
      submitOffer(
        expectedLockVersion: $expectedLockVersion
        offerId: $offerId
        pricePointId: $pricePointId
        host: $host
        proposalOrigin: $proposalOrigin
      ) {
        correlationId
      }
    }
  `;
  return storeFront
    .mutate<{
      submitOffer: {
        correlationId: string;
      };
    }>({
      mutation: submitOfferQuery,
      variables: {
        offerId,
        pricePointId,
        expectedLockVersion,
        host,
        proposalOrigin,
      },
    })
    .then((result) => {
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });
};

type onDomainEventResponse = {
  onDomainEvent: {
    correlationId: string;
    errors: string[];
    occurredOn: string;
  };
};
const onDomainEvent = (correlationId: string) => {
  const onDomainEventQuery = gql`
    subscription onDomainEvent($correlationId: ID) {
      onDomainEvent(correlationId: $correlationId) {
        __typename
        ... on ErrorOccurred {
          __typename
          errors
          correlationId
          occurredOn
        }
        ... on OfferSubmitted {
          __typename
          correlationId
          offerId
          occurredOn
        }
        ... on OffersSynced {
          __typename
          correlationId
          numOffersImported
          numOffersUpdated
          occurredOn
          errors
        }
      }
    }
  `;
  return storeFront.subscribe<onDomainEventResponse>({
    query: onDomainEventQuery,
    variables: {
      correlationId,
    },
  });
};

const getPricePoints = async (offerId: string, role: UserRole) => {
  const query = loader("./getPricing.gql");

  const cashPricePoints = storeFront
    .query<{
      getPricePoints: UnifiedPricingData;
    }>({
      query,
      variables: {
        offerId,
        role,
        financialProduct: CASH,
      },
      fetchPolicy: "no-cache",
    })
    .then((result) => {
      if (result.error) {
        throw result.error;
      }
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });
  const monthlyPricePoints = storeFront
    .query<{
      getPricePoints: UnifiedPricingData;
    }>({
      query,
      variables: {
        offerId,
        role,
        financialProduct: MONTHLY,
      },
      fetchPolicy: "no-cache",
    })
    .then((result) => {
      if (result.error) {
        throw result.error;
      }
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });
  const flexMonthlyPoints = storeFront
    .query<{
      getPricePoints: UnifiedPricingData;
    }>({
      query,
      variables: {
        offerId,
        role,
        financialProduct: FLEX_MONTHLY,
      },
      fetchPolicy: "no-cache",
    })
    .then((result) => {
      if (result.error) {
        throw result.error;
      }
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });
  const prepaidPricePoints = storeFront
    .query<{
      getPricePoints: UnifiedPricingData;
    }>({
      query,
      variables: {
        offerId,
        role,
        financialProduct: PREPAID,
      },
      fetchPolicy: "no-cache",
    })
    .then((result) => {
      if (result.error) {
        throw result.error;
      }
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });
  const loanPricePoints = storeFront
    .query<{
      getPricePoints: UnifiedPricingData;
    }>({
      query,
      variables: {
        offerId,
        role,
        financialProduct: LOAN,
      },
      fetchPolicy: "no-cache",
    })
    .then((result) => {
      if (result.error) {
        throw result.error;
      }
      if (result.errors && result.errors.length > 0) {
        throw result.errors[0];
      }
      return result;
    });

  return Promise.all([
    cashPricePoints,
    monthlyPricePoints,
    prepaidPricePoints,
    loanPricePoints,
    flexMonthlyPoints,
  ]).then((results) => {
    let preferredPricingType = undefined;
    let preferredPricePointId = undefined;
    let preferredPricePointCount = 0;
    if (results[0].data.getPricePoints.preferredPricePointId) {
      preferredPricingType = CASH;
      preferredPricePointId =
        results[0].data.getPricePoints.preferredPricePointId;
      preferredPricePointCount++;
    }
    if (results[1].data.getPricePoints.preferredPricePointId) {
      preferredPricingType = MONTHLY;
      preferredPricePointId =
        results[1].data.getPricePoints.preferredPricePointId;
      preferredPricePointCount++;
    }
    if (results[2].data.getPricePoints.preferredPricePointId) {
      preferredPricingType = PREPAID;
      preferredPricePointId =
        results[2].data.getPricePoints.preferredPricePointId;
      preferredPricePointCount++;
    }
    if (results[3].data.getPricePoints.preferredPricePointId) {
      preferredPricingType = LOAN;
      preferredPricePointId =
        results[3].data.getPricePoints.preferredPricePointId;
      preferredPricePointCount++;
    }
    if (results[4].data.getPricePoints.preferredPricePointId) {
      preferredPricingType = FLEX_MONTHLY;
      preferredPricePointId =
        results[4].data.getPricePoints.preferredPricePointId;
      preferredPricePointCount++;
    }
    if (preferredPricePointCount > 1) {
      logger.error(`Multiple preferred price points found`, {
        metadata: { offerId },
      });
    }
    return {
      preferredPricingType,
      preferredPricePointId,
      cash: results[0].data.getPricePoints.cash,
      monthly: results[1].data.getPricePoints.monthly,
      prepaid: results[2].data.getPricePoints.prepaid,
      loan: results[3].data.getPricePoints.loan,
      flexMonthly: results[4]?.data?.getPricePoints?.flexMonthly,
    } as UnifiedPricingData;
  });
};

export {
  amendOffer,
  amendOfferWithSync,
  copyOffer,
  createOfferInitialize,
  createOfferLines,
  customerGetDesignImages,
  customerGetOffer,
  customerListOffers,
  deleteOffer,
  deleteOfferLines,
  emptyOffer,
  getStoreFrontDesignImages,
  getEditOffer,
  getOffer,
  getPricePoints,
  getPricingOffer,
  getProducts,
  listOffers,
  onDomainEvent,
  reconcileRequiredAccessories,
  storeFront,
  submitOffer,
  syncSalesforce,
  updateOfferLines,
};

export type { UserRole };
